import {
    IJobTaskRunDetail,
    JobTaskStatus,
    JobTaskType,
} from "@/api/admin/admin.types";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import {
    getPhaseDuration,
    getPhaseStartTime,
    isPhaseInProgress,
    TASK_TYPE_LABELS,
} from "../utils";
import { LiveDuration } from "./LiveDuration";
import { TaskRow } from "./TaskRow";

interface TaskTypeGroupProps {
    taskType: JobTaskType;
    tasks: IJobTaskRunDetail[];
}

export const TaskTypeGroup = ({ taskType, tasks }: TaskTypeGroupProps) => {
    const allCompleted = tasks.every(
        (t) => t.status === JobTaskStatus.COMPLETED
    );
    const [expanded, setExpanded] = useState(!allCompleted);

    const completedCount = tasks.filter(
        (t) => t.status === JobTaskStatus.COMPLETED
    ).length;
    const duration = getPhaseDuration(tasks);
    const isLive = isPhaseInProgress(tasks);
    const liveStart = getPhaseStartTime(tasks);
    const label = TASK_TYPE_LABELS[taskType];

    if (tasks.length === 1) {
        return <TaskRow task={tasks[0]} />;
    }

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => setExpanded(!expanded)}
                sx={{
                    py: 1,
                    px: 2.5,
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "#f8fafc" },
                    borderBottom: "1px solid",
                    borderColor: "#f1f5f9",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        transition: "transform 0.2s ease",
                        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                    }}
                >
                    <ChevronDown size={14} />
                </Box>

                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#334155",
                    }}
                >
                    {label}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                >
                    {tasks.length} task{tasks.length !== 1 ? "s" : ""}
                </Typography>

                <Box sx={{ flex: 1 }} />

                <Typography
                    variant="body2"
                    sx={{
                        fontSize: "0.75rem",
                        color: allCompleted ? "#16a34a" : "text.secondary",
                        fontWeight: allCompleted ? 500 : 400,
                    }}
                >
                    {allCompleted
                        ? "all completed"
                        : `${completedCount}/${tasks.length} completed`}
                </Typography>

                {isLive && liveStart ? (
                    <LiveDuration
                        startTime={liveStart}
                        variant="body2"
                        sx={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: "0.75rem",
                            color: "text.secondary",
                        }}
                    />
                ) : duration ? (
                    <Typography
                        variant="body2"
                        sx={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: "0.75rem",
                            color: "text.secondary",
                        }}
                    >
                        {duration}
                    </Typography>
                ) : null}
            </Stack>

            <Collapse in={expanded}>
                <Box>
                    {tasks.map((task) => (
                        <TaskRow key={task.id} task={task} hideTaskType />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};
